/**
 * PlayChess's unmount cleanups, replicated (app index.tsx L2188-2250; web
 * index.js L380-395, L425-440):
 *  - clear the league AND club challenge keys, so a half-finished challenge
 *    cannot mis-attribute the next ordinary game;
 *  - release the tournament gate (`setTournamentPlay(false)`), which
 *    ChallengeComponent reads to suppress incoming challenges;
 *  - leave the tournament ROOM (`stop_watching_tournament_request`) — the
 *    server evicts spectators only, so it is safe from any exit, participant
 *    included (old app L2238-2246);
 *  - reset the ArenaStore: arena mode is screen-scoped, and a later non-arena
 *    visit would otherwise inherit stale status / standings / berserk flags
 *    (old app L2247-2249, `useArenaStore.getState().resetArena()`).
 *
 * ONE ADDITION the old screens do not have: a live GAME watch is stopped too.
 * Neither old screen sends `stop_watching_tournament_game_request` on unmount
 * (they only send it on an explicit stop or a switch, app L5095/L5130), so the
 * socket stayed in the watched game's room for the rest of the session and kept
 * receiving its turn/end events — which on this lane would reduce into the watch
 * slice of a screen that is gone. P1-13.
 *
 * No lane gate on this platform: the web reloads the tab when a lane that has
 * already registered its socket handlers hands over, so nothing else is writing
 * these flags. (The APP copy of this file is byte-identical, but its bridge
 * functions are gated — which is why its V2 root lowers that flag in the LAST
 * effect it declares, after this cleanup has run.)
 */
import {useEffect} from 'react';

import {
  bridgeResetArena,
  bridgeSetTournamentPlay,
} from '@/Store/liveGame/gamePlayStoreWrites';
import {
  clearClubChallengeKeys,
  clearLeagueChallengeKeys,
} from '@/Store/liveGame/handshakes';
import {liveGameStore} from '@/Store/liveGameStore';
import {useV2Ui} from '../theme/useV2Ui';

export function useUnmountCleanup(): void {
  useEffect(() => {
    return () => {
      clearLeagueChallengeKeys();
      clearClubChallengeKeys();
      bridgeSetTournamentPlay(false);

      const state = liveGameStore.getState();
      if (state.watch.active) {
        state.actions.stopWatchingGame();
      }
      // `connected` is the old screen's `if (!tid || !c) return` guard: with no
      // socket the packet is dropped anyway, and the local slice must not be
      // cleared as if the server had been told.
      if (state.tournament.id && state.session.connected) {
        state.actions.stopWatchingTournament();
      }
      bridgeResetArena();
      // A FINISHED game was the old screen's component state: it died with the
      // screen. V2's store survives the unmount and `no_ongoing_game` no longer
      // clears an ended game (reducers/game.ts `noOngoingGame`, fix A1), so this
      // is the only exit for a result the player has already seen — without it a
      // dismissed card + a tab switch strands them on a frozen board (fix A
      // review). A LIVE game is deliberately left warm for the remount.
      if (state.game.phase === 'ended') {
        useV2Ui.getState().setResultDismissed(false);
        state.actions.resetForNewGame();
      }
    };
  }, []);
}

export default useUnmountCleanup;
